// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import SignInPage from './components/SignInPage';
import Dashboard from './pages/Dashboard';
import UserDetails from './components/UserDetails';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import ProtectedRoute from './components/ProtectedRoute';
import CampusMap from './pages/CampusMap';
import SchedulePage from './pages/SchedulePage';
import { AuthProvider } from './context/AuthContext';
import { ScheduleProvider } from './context/ScheduleContext';
import './styles/designSystem.css';

// Component for direct external redirects
const DirectRedirect = ({ url }) => {
  React.useEffect(() => {
    window.location.replace(url);
  }, [url]);
  
  return null;
};

// Development debugging
const isDev = process.env.NODE_ENV === 'development';
if (isDev) {
  console.log('Running in DEVELOPMENT mode');
}

// External site URLs
const SELF_SERVICE_URL = 'https://clgssprod.exchange.viterbo.edu/Student/Account/Login?ReturnUrl=%2FStudent%2F%3FhideProxyDialog%3DFalse';
const VUPORTAL_URL = 'https://viterbou.sharepoint.com/';
const MOODLE_URL = 'https://moodle.viterbo.edu/local/login/index.php';
const DEGREE_PLAN_URL = 'https://clgssprod.exchange.viterbo.edu/Student/Planning/DegreePlans';
const CAFETERIA_URL = 'https://viterbo.mydininghub.com/en/location/the-caf';
const STARFISH_URL = 'https://vitearlyalert.starfishsolutions.com/starfish-ops/support/login.html?tenantId=560';
const ETRIEVE_URL = 'https://etcentral.viterbo.edu/';
const O365_URL = 'https://outlook.office.com/mail/';
const STUDENT_TECH_URL = 'https://libguides.viterbo.edu/studenttech/studenttechguides';
const VITERBO_EDU_URL = 'https://www.viterbo.edu';

function App() {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });

    const registerSW = () => {
      navigator.serviceWorker.register('/vu-connect/service-worker.js')
        .catch(error => {
          console.error('Service Worker registration failed:', error);
        });
    };

    if (document.readyState === 'complete') {
      registerSW();
    } else {
      window.addEventListener('load', registerSW);
      return () => window.removeEventListener('load', registerSW);
    }
  }, []);

  return (
    <AuthProvider>
      <ScheduleProvider>
      <Router basename="/vu-connect">
        <div style={{ minHeight: '100vh' }}>
          <Routes>
            <Route path="/" element={<SignInPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-details"
              element={
                <ProtectedRoute>
                  <UserDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/moodle"
              element={
                <ProtectedRoute>
                  <DirectRedirect url={MOODLE_URL} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/self-service"
              element={
                <ProtectedRoute>
                  <DirectRedirect url={SELF_SERVICE_URL} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/degree-plan"
              element={
                <ProtectedRoute>
                  <DirectRedirect url={DEGREE_PLAN_URL} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cafeteria"
              element={
                <ProtectedRoute>
                  <DirectRedirect url={CAFETERIA_URL} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vuportal"
              element={
                <ProtectedRoute>
                  <DirectRedirect url={VUPORTAL_URL} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/starfish"
              element={
                <ProtectedRoute>
                  <DirectRedirect url={STARFISH_URL} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/etrieve"
              element={
                <ProtectedRoute>
                  <DirectRedirect url={ETRIEVE_URL} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/o365"
              element={
                <ProtectedRoute>
                  <DirectRedirect url={O365_URL} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/student-tech"
              element={
                <ProtectedRoute>
                  <DirectRedirect url={STUDENT_TECH_URL} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/viterbo-edu"
              element={
                <ProtectedRoute>
                  <DirectRedirect url={VITERBO_EDU_URL} />
                </ProtectedRoute>
              }
            />

            {/* New Routes for Events Feature */}
            <Route
              path="/events"
              element={
                <ProtectedRoute>
                  <EventsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/:eventTitle"
              element={
                <ProtectedRoute>
                  <EventDetailPage />
                </ProtectedRoute>
              }
            />

            {/* Campus Map Route */}
            <Route
              path="/campus-map"
              element={
                <ProtectedRoute>
                  <CampusMap />
                </ProtectedRoute>
              }
            />

            {/* Courses & Schedule Route */}
            <Route
              path="/schedule"
              element={
                <ProtectedRoute>
                  <SchedulePage />
                </ProtectedRoute>
              }
            />

            {/* Catch-all route */}
            <Route
              path="*"
              element={
                <div className="signin-container">
                  <div className="signin-card">
                    <h1>404 - Not Found</h1>
                    <p>Path: {window.location.pathname}</p>
                    <button 
                      className="btn-sso" 
                      onClick={() => window.history.back()}
                      style={{ marginTop: '20px' }}
                    >
                      Go Back
                    </button>
                  </div>
                </div>
              }
            />
          </Routes>
        </div>
      </Router>
      </ScheduleProvider>
    </AuthProvider>
  );
}

export default App;